"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

type DiffLineType = "added" | "removed" | "unchanged";

interface DiffLine {
  type: DiffLineType;
  text: string;
}

function computeDiff(original: string, revised: string): DiffLine[] {
  const origLines = original.split("\n");
  const revLines = revised.split("\n");

  // Build LCS table
  const m = origLines.length;
  const n = revLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0)
  );

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (origLines[i - 1] === revLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff
  const result: DiffLine[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && origLines[i - 1] === revLines[j - 1]) {
      result.unshift({ type: "unchanged", text: origLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: revLines[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: origLines[i - 1] });
      i--;
    }
  }

  return result;
}

function lineClass(type: DiffLineType): string {
  if (type === "added") {
    return "bg-green-500/10 text-green-800 dark:text-green-300 border-l-2 border-green-500";
  }
  if (type === "removed") {
    return "bg-red-500/10 text-red-800 dark:text-red-300 border-l-2 border-red-500 line-through";
  }
  return "text-muted-foreground";
}

function linePrefix(type: DiffLineType): string {
  if (type === "added") {
    return "+ ";
  }
  if (type === "removed") {
    return "- ";
  }
  return "  ";
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_DIFF_CHECKER_STRINGS = {
  originalText: "Original text",
  pasteTheOriginalTextHere: "Paste the original text here...",
  revisedText: "Revised text",
  pasteTheRevisedTextHere: "Paste the revised text here...",
  compare: "Compare",
  clear: "Clear",
  linesAdded: "Lines added",
  linesRemoved: "Lines removed",
  unchanged: "Unchanged",
  added: "Added",
  removed: "Removed",
} as const;

export default function TextDiffCheckerWidget() {
  const [original, setOriginal] = useState("");
  const [revised, setRevised] = useState("");
  const [diff, setDiff] = useState<DiffLine[] | null>(null);

  function handleCompare() {
    setDiff(computeDiff(original, revised));
  }

  function handleClear() {
    setOriginal("");
    setRevised("");
    setDiff(null);
  }

  const added = diff ? diff.filter((l) => l.type === "added").length : 0;
  const removed = diff ? diff.filter((l) => l.type === "removed").length : 0;
  const unchanged = diff
    ? diff.filter((l) => l.type === "unchanged").length
    : 0;

  const strings = useToolUi(TEXT_DIFF_CHECKER_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Two-column input */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="diff-original"
          >
            {strings.originalText}
          </label>
          <textarea
            className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="diff-original"
            onChange={(e) => {
              setOriginal(e.target.value);
              setDiff(null);
            }}
            placeholder={strings.pasteTheOriginalTextHere}
            value={original}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="diff-revised"
          >
            {strings.revisedText}
          </label>
          <textarea
            className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="diff-revised"
            onChange={(e) => {
              setRevised(e.target.value);
              setDiff(null);
            }}
            placeholder={strings.pasteTheRevisedTextHere}
            value={revised}
          />
        </div>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={original.trim().length === 0 && revised.trim().length === 0}
          onClick={handleCompare}
          type="button"
        >
          {strings.compare}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={original.length === 0 && revised.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Stats */}
      {diff !== null && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={strings.linesAdded} value={fmt(added)} />
          <StatCard label={strings.linesRemoved} value={fmt(removed)} />
          <StatCard label={strings.unchanged} value={fmt(unchanged)} />
        </div>
      )}
      {/* Diff output */}
      {diff !== null && (
        <section className="space-y-3">
          <div className="flex items-center gap-4 text-muted-foreground text-xs">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm border border-green-500 bg-green-500/20" />
              {strings.added}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm border border-red-500 bg-red-500/20" />
              {strings.removed}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm border border-border bg-muted" />
              {strings.unchanged}
            </span>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <pre className="overflow-x-auto p-4 font-mono text-sm leading-relaxed">
              {diff.map((line, idx) => (
                <div
                  className={`px-2 py-0.5 ${lineClass(line.type)}`}
                  key={`${idx}-${line.type}`}
                >
                  <span aria-hidden="true">{linePrefix(line.type)}</span>
                  {line.text || "\u00a0"}
                </div>
              ))}
            </pre>
          </div>
        </section>
      )}
    </>
  );
}
